import { useTranslation } from 'react-i18next';
import type { SWRConfiguration } from 'swr';
import { mutate } from 'swr';

import useHandleError from 'hooks/useHandleError';
import useShowAlert from 'hooks/useShowAlert';
import { checkStatus, post, put } from 'libs/apiClient';
import type {
  AccessControlGroup,
  AccessControlModule,
  AccessLevelOption,
  AccessMatrix,
  CurrentUserModule,
  NewAccessControlGroup,
} from 'model/AccessControl/types';
import type { UserGroup } from 'model/User/types';

import { useApi } from './base-api';

export const accessGroupsBaseUrl = '/api/access-groups';
export const accessMatrixBaseUrl = '/api/access-groups/matrix';

export const useGetAccessGroups = () =>
  useApi<AccessControlGroup[]>(accessGroupsBaseUrl);

export const useGetAccessGroup = (id: string, config?: SWRConfiguration) =>
  useApi<AccessControlGroup>(
    id ? `${accessGroupsBaseUrl}/${id}` : null,
    config
  );

export const useCreateAccessGroup = () => {
  const { t } = useTranslation();
  const handleError = useHandleError();
  const { showSuccessMessage } = useShowAlert();

  return async (newGroup: NewAccessControlGroup) => {
    await mutate<AccessControlGroup[]>(
      accessGroupsBaseUrl,
      async (data) => {
        try {
          const response = checkStatus(
            await post(accessGroupsBaseUrl, newGroup)
          );
          showSuccessMessage({
            message: t('AccessControl.AccessGroup.Created', {
              name: newGroup.name,
            }),
          });

          const newAccessGroup: AccessControlGroup = await response.json();

          if (data) {
            return [...data, newAccessGroup];
          }

          return [newAccessGroup];
        } catch (error) {
          handleError(error);
          throw error;
        }
      },
      {
        revalidate: false,
      }
    );
  };
};

export const useUpdateAccessGroup = () => {
  const { t } = useTranslation();
  const handleError = useHandleError();
  const { showSuccessMessage } = useShowAlert();

  return async (updatedGroup: AccessControlGroup) => {
    await mutate<AccessControlGroup[]>(
      accessGroupsBaseUrl,
      async (data) => {
        try {
          const response = checkStatus(
            await put(`${accessGroupsBaseUrl}/${updatedGroup.id}`, updatedGroup)
          );
          showSuccessMessage({
            message: t('AccessControl.AccessGroup.Updated', {
              name: updatedGroup.name,
            }),
          });

          const updatedAccessGroup: AccessControlGroup = await response.json();

          if (data) {
            return data.map((group) =>
              group.id === updatedGroup.id ? updatedAccessGroup : group
            );
          }

          return [updatedAccessGroup];
        } catch (error) {
          handleError(error);
          throw error;
        }
      },
      {
        revalidate: false,
        optimisticData: (data) =>
          data
            ? data.map((group) =>
                group.id === updatedGroup.id ? updatedGroup : group
              )
            : [updatedGroup],
      }
    );
  };
};

export const useDeleteAccessGroup = () => {
  const { t } = useTranslation();
  const handleError = useHandleError();
  const { showSuccessMessage } = useShowAlert();

  return async (group: AccessControlGroup) => {
    await mutate<AccessControlGroup[]>(
      accessGroupsBaseUrl,
      async (data) => {
        try {
          checkStatus(await post(accessGroupsBaseUrl, group));
          showSuccessMessage({
            message: t('AccessControl.AccessGroup.Deleted', {
              name: group.name,
            }),
          });

          if (data) {
            return data.filter((g) => g.id !== group.id);
          }

          return [];
        } catch (error) {
          handleError(error);
          throw error;
        }
      },
      {
        revalidate: false,
      }
    );
  };
};

export const useDisableAccessGroup = () => {
  const { t } = useTranslation();
  const handleError = useHandleError();
  const { showSuccessMessage } = useShowAlert();

  return async (group: AccessControlGroup) => {
    await mutate<AccessControlGroup[]>(
      accessGroupsBaseUrl,
      async (data) => {
        try {
          const response = checkStatus(await post(accessGroupsBaseUrl, group));
          showSuccessMessage({
            message: t('AccessControl.AccessGroup.Disabled', {
              name: group.name,
            }),
          });

          const updatedAccessGroup: AccessControlGroup = await response.json();

          if (data) {
            return data.map((g) =>
              g.id === updatedAccessGroup.id ? updatedAccessGroup : g
            );
          }

          return [updatedAccessGroup];
        } catch (error) {
          handleError(error);
          throw error;
        }
      },
      {
        revalidate: false,
        optimisticData: (data) =>
          data
            ? data.map((g) =>
                g.id === group.id ? { ...g, isDisabled: true } : g
              )
            : [],
      }
    );
  };
};

export const useGetAccessModules = (config?: SWRConfiguration) =>
  useApi<AccessControlModule[]>(`${accessGroupsBaseUrl}/modules`, config);

interface AccessMatrixParams {
  baseRole?: UserGroup;
  userId?: string;
  accessGroupId?: string;
}
export const useGetAccessMatrix = (
  { baseRole, userId, accessGroupId }: AccessMatrixParams = {},
  config?: SWRConfiguration
) => {
  const params = new URLSearchParams();
  if (baseRole && !accessGroupId) {
    params.set('baseRole', baseRole);
  }
  if (userId) {
    params.set('userId', userId);
  }
  if (accessGroupId) {
    params.set('accessGroupId', accessGroupId);
  }

  return useApi<AccessMatrix>(
    `${accessMatrixBaseUrl}${params.toString() ? `?${params.toString()}` : ''}`,
    config
  );
};

export const useGetCurrentUserModules = (config?: SWRConfiguration) =>
  useApi<CurrentUserModule[]>(
    `${accessGroupsBaseUrl}/current-user/modules`,
    config
  );

export const useGetAccessLevelOptions = () =>
  useApi<AccessLevelOption[]>(`${accessGroupsBaseUrl}/access-level-options`);
